import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowUp, Sparkles } from 'lucide-react';
import { portalQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { hasPermission } from '@blimo/shared';

const MAX_LENGTH = 4000;

/**
 * Blimo-AI-Eingabe auf der Startseite: die Frage öffnet den Chat (/assistent?q=…) und wird dort direkt gestellt.
 * Nur sichtbar mit `assistant.use` und aktiviertem Assistenten. Bewusst ohne Abhängigkeit zur (lazy geladenen) Chat-Seite.
 *
 * @param {{ me: any }} props
 */
export function HomeAssistantBar({ me }) {
  const api = useApi();
  const navigate = useNavigate();
  const id = useId();
  const [value, setValue] = useState('');
  const allowed = hasPermission(me, 'assistant.use');
  const status = useQuery({ ...portalQueries.assistantStatus(api), enabled: allowed });
  if (!allowed || !status.data?.enabled) return null;

  const suggestions = (status.data.suggestions ?? []).slice(0, 3);

  /** @param {string} text */
  function ask(text) {
    const q = text.trim();
    if (!q || q.length > MAX_LENGTH) return;
    navigate({ to: '/assistent', search: { q } });
  }

  /** @param {string} s */
  function pick(s) {
    if (s.includes('…')) {
      setValue(s.replace(/\s*…\s*\??\s*$/, ' ').replace(/…/g, ''));
      document.getElementById(id)?.focus();
      return;
    }
    ask(s);
  }

  return (
    <section aria-label="Blimo AI fragen" className="flex flex-col gap-2">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          ask(value);
        }}
        className="flex h-10 items-center gap-2 rounded-lg border bg-background pl-3 pr-1 shadow-xs transition-shadow focus-within:border-[color-mix(in_oklab,var(--primary)_70%,var(--border))] focus-within:shadow-[0_0_0_3px_color-mix(in_oklab,var(--primary)_18%,transparent)]"
      >
        <Sparkles className="size-4 shrink-0 text-(--primary-text)" aria-hidden="true" />
        <label htmlFor={id} className="sr-only">
          Frage an Blimo AI
        </label>
        <input
          id={id}
          type="text"
          value={value}
          maxLength={MAX_LENGTH}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Blimo AI fragen – z. B. zu offenen Posten, Mietern oder Dokumenten"
          enterKeyHint="send"
          autoComplete="off"
          className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground focus-visible:outline-none! max-sm:text-base"
        />
        <kbd className="hidden rounded border bg-muted px-1.5 font-sans text-[0.6875rem] leading-5 text-muted-foreground sm:inline" aria-hidden="true">
          Enter
        </kbd>
        <button
          type="submit"
          disabled={!value.trim()}
          aria-label="Frage senden"
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-opacity disabled:opacity-35"
        >
          <ArrowUp className="size-4" aria-hidden="true" />
        </button>
      </form>
      {suggestions.length > 0 && (
        <ul aria-label="Vorschläge" className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none]">
          {suggestions.map((s) => (
            <li key={s} className="shrink-0">
              <button
                type="button"
                onClick={() => pick(s)}
                className="whitespace-nowrap rounded-full border bg-background px-2.5 py-1 text-xs leading-4 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
